<template>
  <div class="panel panel-default">
    <div class="panel-heading">
      {{ lang.RCC000 }}
      <span
        id="checkCounterContainer"
        :class="{ 'text-red': checkCountExceeded }"
        v-if="typeof resultCheckData.SurveyInstanceCount === 'number'"
      >
        <span
          >{{ lang.RCC001 }}: {{ resultCheckData.SurveyInstanceCount }}</span
        >
        <i v-show="checkCountExceeded" class="material-icons md-24">warning</i>
      </span>
      <b-tooltip
        target="checkCounterContainer"
        v-if="checkCountExceeded"
        :delay="globals.tooltipDelay"
        boundary="viewport"
        placement="bottom"
        >{{ lang.RCC021 }}</b-tooltip
      >
    </div>
    <div class="panel-body">
      <div
        class="form-group col-md-12"
        id="selectCheckFilterClientFormGroup"
        v-if="!feature.isAudiTarget || !feature.isEnabled('audi_importers')"
      >
        <select2
          id="selectCheckFilterClient"
          ref="clientSelect"
          v-model="selectedClient"
          :placeholder="`${lang.RCC002}...`"
          :allowClear="true"
        >
          <option></option>
          <option
            v-for="client in clientList"
            :key="client.ClientGuid"
            :value="client.ClientGuid"
          >
            {{ client.ClientName }}
          </option>
        </select2>
        <b-tooltip
          target="selectCheckFilterClientFormGroup"
          boundary="viewport"
          triggers="hover"
          :delay="globals.tooltipDelay"
          >{{ lang.RCC002 }}</b-tooltip
        >
      </div>
      <div
        class="form-group col-md-12"
        id="selectCheckFilterImporterFormGroup"
        v-else
      >
        <select2
          id="selectCheckFilterImporter"
          ref="importerSelect"
          v-model="selectedImporter"
          :afterChange="selectedImporterChanged"
          :placeholder="`${lang.RCC022}...`"
          :allowClear="true"
        >
          <option
            v-for="importer in importerList"
            :key="importer.ImporterGuid"
            :value="importer.ImporterGuid"
          >
            {{ importer.ImporterName }}
          </option>
        </select2>
        <b-tooltip
          target="selectCheckFilterImporterFormGroup"
          boundary="viewport"
          triggers="hover"
          :delay="globals.tooltipDelay"
          >{{ lang.RCC022 }}</b-tooltip
        >
      </div>
      <div
        class="form-group col-md-6"
        id="selectCheckFilterProjectFormGroup"
        v-if="selectedClient"
      >
        <select2
          id="selectCheckFilterProject"
          ref="projectSelect"
          v-model="selectedProject"
          :placeholder="`${lang.RCC003}...`"
          :allowClear="true"
        >
          <option
            v-for="project in projectsOfSelectedClient"
            :key="project.ProjectGuid"
            :value="project.ProjectGuid"
          >
            {{ project.ProjectName }}
          </option>
        </select2>
        <b-tooltip
          target="selectCheckFilterProjectFormGroup"
          boundary="viewport"
          triggers="hover"
          :delay="globals.tooltipDelay"
          >{{ lang.RCC003 }}</b-tooltip
        >
      </div>
      <div
        class="form-group col-md-6"
        id="selectCheckFilterWaveFormGroup"
        v-if="selectedProject"
      >
        <select2
          id="selectCheckFilterWave"
          ref="waveSelect"
          v-model="selectedWave"
          :placeholder="`${lang.RCC004}...`"
          :allowClear="true"
        >
          <option
            v-for="wave in wavesOfSelectedProject"
            :key="wave.WaveGuid"
            :value="wave.WaveGuid"
          >
            {{ wave.WaveName }}
          </option>
        </select2>
        <b-tooltip
          target="selectCheckFilterWaveFormGroup"
          boundary="viewport"
          triggers="hover"
          :delay="globals.tooltipDelay"
          >{{ lang.RCC004 }}</b-tooltip
        >
      </div>

      <template v-if="selectedWave">
        <div
          class="form-group form-group-dynamic"
          v-for="filter in dynamicFilters"
          :key="filter.Guid"
        >
          <div
            class="form-group col-md-3"
            :id="`selectCheckFilterDynamicTypeFormGroup${filter.Guid}`"
          >
            <select2
              v-model="filter.Type"
              :ref="`filterTypeSelect${filter.Guid}`"
              :id="`selectCheckFilterDynamicType${filter.Guid}`"
              :placeholder="`${lang.RCC005}...`"
              :allowClear="true"
              :afterChange="
                (t) => {
                  filterTypeChanged(t, filter);
                }
              "
            >
              <option
                v-for="(type, idx) in filterTypes"
                :key="idx"
                :value="type"
                :disabled="!isTypeEnabled(type, filter)"
              >
                {{ getTranslationFor(type) }}
              </option>
            </select2>
            <b-tooltip
              :target="`selectCheckFilterDynamicTypeFormGroup${filter.Guid}`"
              boundary="viewport"
              triggers="hover"
              :delay="globals.tooltipDelay"
              >{{ lang.RCC006 }}</b-tooltip
            >
          </div>
          <span
            class="vertical-center"
            v-if="
              filter.Type === 'Region' &&
              filter.currentDynamicFilterValues &&
              filter.currentDynamicFilterValues.length < 1
            "
            >{{ lang.RCC026 }}</span
          >
          <div
            class="form-group col-md-8"
            v-else-if="filter.secondLevelSelectVisible"
            :id="`selectCheckFilterDynamicValueFormGroup${filter.Guid}`"
          >
            <select2
              v-model="filter.Value"
              :key="filter.Type"
              :ref="`secondLevelSelect${filter.Guid}`"
              :id="`selectCheckFilterDynamicValue${filter.Guid}`"
              :placeholder="`${lang.RCC005}...`"
              :allowClear="true"
              :multiple="filter.Type !== 'LocationProperty'"
              :tags="
                filter.Type === 'City' ||
                filter.Type === 'PostalCode' ||
                filter.Type === 'Location' ||
                filter.Type === 'Check'
              "
              :noResultsString="
                filter.Type === 'City' ||
                filter.Type === 'PostalCode' ||
                filter.Type === 'Location' ||
                filter.Type === 'Check'
                  ? lang.RCC019
                  : undefined
              "
              :afterChange="
                (v) => {
                  filterValueChanged(v, filter);
                }
              "
            >
              <template v-if="filter.Type === 'City'">
                <option
                  v-for="(value, idx) in filter.currentDynamicFilterValues"
                  :key="idx"
                  :value="value.City"
                >
                  {{ value.City }}
                </option>
              </template>
              <template v-else-if="filter.Type === 'Country'">
                <option
                  v-for="value in filter.currentDynamicFilterValues"
                  :key="value[`${filter.Type}Code`]"
                  :value="value[`${filter.Type}Code`]"
                >
                  {{ value[`${filter.Type}Name`] }}
                </option>
              </template>
              <template v-else-if="filter.Type === 'Status'">
                <option
                  v-for="(value, idx) in filter.currentDynamicFilterValues"
                  :key="idx"
                  :value="value"
                >
                  {{ getTranslationFor(value) }}
                </option>
              </template>
              <template v-else>
                <option
                  v-for="value in filter.currentDynamicFilterValues"
                  :disabled="propertyAlreadyUsed(value[`${filter.Type}Guid`])"
                  :key="value[`${filter.Type}Guid`]"
                  :value="value[`${filter.Type}Guid`]"
                >
                  {{ value[`${filter.Type}Name`] }}
                </option>
              </template>
            </select2>
            <b-tooltip
              :target="`selectCheckFilterDynamicValueFormGroup${filter.Guid}`"
              boundary="viewport"
              triggers="hover"
              :delay="globals.tooltipDelay"
              >{{ getTranslationFor(filter.Type) }}</b-tooltip
            >
          </div>
          <div
            class="form-group col-md-11"
            v-if="filter.thirdLevelSelectVisible"
            :id="`selectCheckFilterDynamicValueValuesFormGroup${filter.Guid}`"
          >
            <select2
              v-model="filter.Subvalue"
              :ref="`thirdLevelSelect${filter.Guid}`"
              :id="`selectCheckFilterDynamicValueValues${filter.Guid}`"
              :placeholder="`${lang.RCC005}...`"
              :allowClear="true"
              :multiple="true"
              :afterChange="
                (s) => {
                  filterSubvalueChanged(s, filter);
                }
              "
            >
              <option
                v-for="value in filter.currentDynamicFilterValueValues"
                :key="value[`${filter.Type}Value`]"
                :value="value[`${filter.Type}Value`]"
              >
                {{ value[`${filter.Type}Value`] }}
              </option>
            </select2>
            <b-tooltip
              :target="`selectCheckFilterDynamicValueValuesFormGroup${filter.Guid}`"
              boundary="viewport"
              triggers="hover"
              :delay="globals.tooltipDelay"
              >{{ lang.RCC007 }}</b-tooltip
            >
          </div>

          <div
            class="col-md-1 pull-right"
            :class="{
              'centerized-remove-button': filter.thirdLevelSelectVisible,
            }"
          >
            <button
              type="button"
              class="btn btn-remove-filter"
              @click="removeDynamicFilter(filter)"
            >
              <i class="material-icons md-24 text-red">clear</i>
            </button>
          </div>
        </div>

        <button
          type="button"
          id="addDynamicFilterButton"
          class="btn btn-fab btn-fab-mini btn-default btn-add-filter"
          :disabled="isLastDynamicFilterEmpty()"
          @click="addDynamicFilter()"
        >
          <i class="material-icons md-36 text-green">add</i>
        </button>
        <b-tooltip
          target="addDynamicFilterButton"
          boundary="viewport"
          triggers="hover"
          :delay="globals.tooltipDelay"
          v-if="!isLastDynamicFilterEmpty()"
          >{{ lang.RCC008 }}</b-tooltip
        >
      </template>
    </div>
  </div>
</template>

<script>
import Select2 from "../Tools/Select2";

const uuidv4 = require("uuid/v4");

export default {
  name: "CheckFilter",
  components: {
    Select2,
  },
  props: {
    currentFilterObject: {
      Type: Object,
      default: () => ({}),
    },
    locationPropertiesList: {
      Type: Array,
      default: () => [],
    },
    countryList: {
      Type: Array,
      default: () => [],
    },
    regionList: {
      Type: Array,
      default: () => [],
    },
    locationList: {
      Type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedClient: "",
      selectedProject: "",
      selectedWave: "",
      selectedImporter: "",
      clientList: [],
      importerList: [],
      projectList: [],
      waveList: [],
      scenarioList: [],
      // cityLists: [],
      dynamicFilters: [],
      filterTypes: [
        "Country",
        "Region",
        "City",
        "PostalCode",
        "Location",
        "LocationProperty",
        "Scenario",
        "Status",
        "Check",
      ],
      recruitingStatusList: [
        "NOT_ASSIGNED",
        "ASSIGNED_WITH_ANSWER",
        "ASSIGNED_NO_ANSWER",
      ],
      resultCheckData: {},
      lang: {
        RCC000: "Check filter",
        RCC001: "Available Checks",
        RCC002: "Client",
        RCC003: "Project",
        RCC004: "Wave",
        RCC005: "Choose",
        RCC006: "Type of filter",
        RCC007: "Select values",
        RCC008: "Add filter",
        RCC009: "Check data request failed",
        RCC010: "City data request failed",
        RCC011: "City",
        RCC012: "Country",
        RCC013: "Location property",
        RCC014: "Scenario",
        RCC015: "Status",
        RCC016: "Not assigned",
        RCC017: "Edited",
        RCC018: "Assigned but not edited",
        RCC019: "Please type in a value",
        RCC020: "Postal code",
        RCC021:
          "The maximum count of checks that can be shown on the map is exceeded. If you want to see all results on the map, please define some more filters.",
        RCC022: "Importer",
        RCC023: "Region",
        RCC024: "Location",
        RCC025: "Check",
        RCC026: "No regions available",
      },
    };
  },
  computed: {
    projectsOfSelectedClient() {
      let entry = _.find(
        this.projectList,
        (el) => el.ClientGuid === this.selectedClient
      );
      if (entry && entry.Projects) return entry.Projects;
      return [];
    },
    wavesOfSelectedProject() {
      let entry = _.find(
        this.waveList,
        (el) => el.ProjectGuid === this.selectedProject
      );
      if (entry && entry.Waves) return entry.Waves;
      return [];
    },
    scenariosOfSelectedWave() {
      let entry = _.find(
        this.scenarioList,
        (el) => el.WaveGuid === this.selectedWave
      );
      if (entry && entry.Scenarios) return entry.Scenarios;
      return [];
    },
    checkCountExceeded() {
      if (
        !this.resultCheckData ||
        _.isEmpty(this.resultCheckData) ||
        !this.resultCheckData.SurveyInstances
      )
        return false;
      return (
        this.resultCheckData.SurveyInstanceCount >
        this.resultCheckData.SurveyInstances.length
      );
    },
    // isCountryFilterDefined() {
    //   for (let filter of this.dynamicFilters) {
    //     if (
    //       filter.Type === "Country" &&
    //       filter.Value &&
    //       filter.Value.length > 0
    //     )
    //       return true;
    //   }
    //   return false;
    // }
    regionsFiltered() {
      let filteredRegionsList = [];

      let countryFilterIndex = _.findIndex(this.dynamicFilters, {
        Type: "Country",
      });
      if (countryFilterIndex > -1) {
        // filter regions on multiple countries
        this.dynamicFilters[countryFilterIndex].Value.forEach((countryCode) => {
          filteredRegionsList = _.union(
            filteredRegionsList,
            this.getRegionsByCountryCode(countryCode)
          );
        });

        // order regions alphabetic
        if (filteredRegionsList)
          filteredRegionsList = _.sortBy(filteredRegionsList, [
            function (o) {
              return o.RegionName;
            },
          ]);
      }
      // // check if selected region in regions of selected countries
      let regionFilterIndex = _.findIndex(this.dynamicFilters, {
        Type: "Region",
      });
      if (
        regionFilterIndex > -1 &&
        this.dynamicFilters[regionFilterIndex].Value
      ) {
        this.dynamicFilters[regionFilterIndex].Value = this.dynamicFilters[
          regionFilterIndex
        ].Value.filter((value) => {
          return (
            _.findIndex(filteredRegionsList, function (cDFV) {
              return cDFV.RegionGuid === value;
            }) >= 0
          );
        });
      }

      return filteredRegionsList;
    },
  },
  watch: {
    "globals.selectedLanguage"() {
      this.globals.getLanguageStrings(this, this.debouncedUpdateSelect2Data);
    },
    selectedClient(newClient) {
      if (!this.$filterRestorePending) this.selectedProject = "";
      this.getLocationProperties();
      // this.getLocationsOfClient();
    },
    selectedProject() {
      if (!this.$filterRestorePending) this.selectedWave = "";
    },
    selectedWave(newGuid) {
      if (!this.$filterRestorePending)
        _.forEach(this.dynamicFilters, (f) => {
          if (f.Type === "Scenario") {
            delete f.Value;
            f.currentDynamicFilterValues = this.scenariosOfSelectedWave;
          }
        });

      if (newGuid) this.getCheckFilterResult();
      else this.resultCheckData = {};
    },
    resultCheckData(newData) {
      this.$emit("checkDataChanged", newData);
    },
  },
  created() {
    this.debouncedUpdateSelect2Data = _.debounce(this.updateSelect2Data, 500);
  },
  mounted() {
    this.readComponentSettingsFromStore();
    if (this.globals.selectedLanguage)
      this.globals.getLanguageStrings(this, this.debouncedUpdateSelect2Data);
    let clientListPromise = this.globals.getClientList();
    clientListPromise.then((data) => {
      this.clientList = data;
      if (
        this.feature.isAudiTarget &&
        this.feature.isEnabled("audi_importers") &&
        this.clientList[0]
      )
        this.selectedClient = this.clientList[0].ClientGuid;
      this.$emit("ClientListReceived");
    });
    this.globals.getProjectList((data) => {
      this.projectList = data;
      this.$emit("ProjectListReceived");
    });
    this.globals.getWaveList((data) => {
      this.waveList = data;
      this.$emit("WaveListReceived");
    });
    this.globals.getScenarioList((data) => {
      this.scenarioList = data;
      this.$emit("ScenarioListReceived");
    });

    if (this.feature.isAudiTarget && this.feature.isEnabled("audi_importers")) {
      this.globals.getImporterList((data) => {
        this.importerList = data;
        this.$emit("ImporterListReceived");
      });
    }
  },
  destroyed() {
    this.writeComponentSettingsToStore();
  },
  methods: {
    filterSubvalueChanged(value, filter) {
      filter.Subvalue = value;
      this.getCheckFilterResult();
    },
    filterValueChanged(value, filter) {
      filter.Value = value;
      this.dynamicFilterValueChanged(filter);
    },
    filterTypeChanged(value, filter) {
      filter.Type = value;
      this.dynamicFilterTypeChanged(filter);
    },
    selectedImporterChanged() {
      this.selectedWave && this.getCheckFilterResult();
    },
    readComponentSettingsFromStore() {
      if (!this.$store.getters["userSettings/rememberFilters"]) return;

      this.$filterRestorePending = true;
      let restoreDone = () => {
        this.$nextTick(() => {
          delete this.$filterRestorePending;
          this.debouncedUpdateSelect2Data();
        });
      };

      let restoreFilters = () => {
        this.$nextTick(() => {
          let storedData =
            this.$store.getters["localFilters/recruiting_checkFilter"];
          if (storedData) {
            for (let key in storedData) {
              if (_.has(this, key)) this[key] = _.cloneDeep(storedData[key]);
            }
          }

          if (!this.$store.getters["userSettings/syncFilters"]) {
            restoreDone();
            return;
          }
          if (
            !this.feature.isAudiTarget ||
            !this.feature.isEnabled("audi_importers")
          ) {
            let storedClient = this.$store.getters["sharedFilters/client"];
            if (this.selectedClient !== storedClient.ClientGuid) {
              this.selectedWave = "";
              this.dynamicFilters = [];
            }
            this.selectedClient = storedClient.ClientGuid || "";
          } else {
            let storedImporter = this.$store.getters["sharedFilters/importer"];
            this.selectedImporter = storedImporter.ImporterGuid || "";
          }

          let storedProject = this.$store.getters["sharedFilters/project"];
          if (this.selectedProject !== storedProject.ProjectGuid) {
            this.selectedWave = "";
            this.dynamicFilters = [];
          }
          this.selectedProject = storedProject.ProjectGuid || "";

          restoreDone();
        });
      };

      let eventsToWaitFor = [
        "ClientListReceived",
        "ProjectListReceived",
        "WaveListReceived",
        "ScenarioListReceived",
      ];

      if (this.feature.isAudiTarget)
        eventsToWaitFor.push("ImporterListReceived");

      if (this.countryList.length < 1) {
        eventsToWaitFor.push("CountryListReceived");
        this.$parent.$once("CountryListReceived", () =>
          this.$emit("CountryListReceived")
        );
      }

      if (this.regionList.length < 1) {
        eventsToWaitFor.push("RegionListReceived");
        this.$parent.$once("RegionListReceived", () => {
          this.$emit("RegionListReceived");
        });
      }

      if (this.selectedClient && this.locationList.length < 1) {
        eventsToWaitFor.push("LocationsReceived");
        this.$parent.$once("LocationsReceived", () =>
          this.$emit("LocationsReceived")
        );
      }

      if (this.selectedClient && this.locationPropertiesList.length < 1) {
        eventsToWaitFor.push("LocationPropertiesReceived");
        this.$parent.$once("LocationPropertiesReceived", () =>
          this.$emit("LocationPropertiesReceived")
        );
      }

      this.globals.doAfterEvents(eventsToWaitFor, restoreFilters, this);
    },
    writeComponentSettingsToStore() {
      if (!this.$store.getters["userSettings/rememberFilters"]) return;
      this.$store.commit("localFilters/recruiting_checkFilter", {
        selectedClient: this.selectedClient,
        selectedImporter: this.selectedImporter,
        selectedProject: this.selectedProject,
        selectedWave: this.selectedWave,
        dynamicFilters: this.dynamicFilters,
      });

      if (!this.$store.getters["userSettings/syncFilters"]) return;
      if (
        !this.feature.isAudiTarget ||
        !this.feature.isEnabled("audi_importers")
      ) {
        if (this.selectedClient) {
          let client = _.find(this.clientList, (c) => {
            return c.ClientGuid === this.selectedClient;
          });
          if (client) this.$store.commit("sharedFilters/client", client);
        } else {
          let storedClient = this.$store.getters["sharedFilters/client"];
          if (storedClient.ClientGuid)
            this.$store.commit("sharedFilters/client");
        }
      } else {
        if (this.selectedImporter) {
          let importer = _.find(this.importerList, (i) => {
            return i.ImporterGuid === this.selectedImporter;
          });
          if (importer) this.$store.commit("sharedFilters/importer", importer);
        } else {
          let storedImporter = this.$store.getters["sharedFilters/importer"];
          if (storedImporter.ImporterGuid)
            this.$store.commit("sharedFilters/importer");
        }
      }

      if (this.selectedProject) {
        let project = _.find(this.projectsOfSelectedClient, (p) => {
          return p.ProjectGuid === this.selectedProject;
        });
        if (project) this.$store.commit("sharedFilters/project", project);
      } else {
        let storedProject = this.$store.getters["sharedFilters/project"];
        if (storedProject.ProjectGuid)
          this.$store.commit("sharedFilters/project");
      }
    },
    updateSelect2Data() {
      for (let filter of this.dynamicFilters) {
        if (filter.Type === "Country")
          filter.currentDynamicFilterValues = this.countryList;
        else if (filter.Type === "Status")
          filter.currentDynamicFilterValues = this.recruitingStatusList;
        else if (filter.Type === "LocationProperty")
          filter.currentDynamicFilterValues = this.locationPropertiesList;
        else if (filter.Type === "Scenario") {
          filter.currentDynamicFilterValues = this.scenariosOfSelectedWave;
          if (filter.Value)
            _.remove(filter.Value, (selectedValue) => {
              return (
                _.findIndex(
                  filter.currentDynamicFilterValues,
                  (val) => val.ScenarioGuid === selectedValue
                ) < 0
              );
            });
        }
      }
      this.$forceUpdate();

      this.$nextTick(() => {
        for (let refName in this.$refs) {
          if (this.$refs[refName][0])
            for (let component of this.$refs[refName]) {
              if (component.reinit) component.reinit();
            }
          else if (this.$refs[refName].reinit) this.$refs[refName].reinit();
        }
      });

      this.getCheckFilterResult();
    },
    updateSelect2Values() {
      for (let refName in this.$refs) {
        if (this.$refs[refName][0])
          for (let component of this.$refs[refName]) {
            if (component.updateValue) component.updateValue();
          }
        else if (this.$refs[refName].updateValue)
          this.$refs[refName].updateValue();
      }
    },
    getTranslationFor(text) {
      switch (text) {
        case "City":
          return this.lang.RCC011;
        case "Country":
          return this.lang.RCC012;
        case "Region":
          return this.lang.RCC023;
        case "PostalCode":
          return this.lang.RCC020;
        case "LocationProperty":
          return this.lang.RCC013;
        case "Location":
          return this.lang.RCC024;
        case "Scenario":
          return this.lang.RCC014;
        case "Status":
          return this.lang.RCC015;
        case "NOT_ASSIGNED":
          return this.lang.RCC016;
        case "ASSIGNED_WITH_ANSWER":
          return this.lang.RCC017;
        case "ASSIGNED_NO_ANSWER":
          return this.lang.RCC018;
        case "Check":
          return this.lang.RCC025;
      }
      return text;
    },
    clearCheckFilter() {
      if (!this.feature.isAudiTarget) this.selectedClient = "";
      else {
        this.selectedImporter = "";
        this.selectedProject = "";
      }
      this.dynamicFilters = [];
      this.resultCheckData = {};
    },
    addDynamicFilter() {
      this.dynamicFilters.push({
        Guid: uuidv4(),
        Type: "",
      });
    },
    removeDynamicFilter(filter) {
      if (filter.Type === "Country") {
        _.remove(this.dynamicFilters, (f) => f.Type === "City");
        _.remove(this.dynamicFilters, (f) => f.Type === "Region");
      }
      _.remove(this.dynamicFilters, (f) => f.Guid === filter.Guid);
      this.getCheckFilterResult();
      this.$forceUpdate();
      this.updateSelect2Values();
    },
    isLastDynamicFilterEmpty() {
      if (this.dynamicFilters.length < 1) return false;
      return !this.dynamicFilters[this.dynamicFilters.length - 1].Type;
    },
    propertyAlreadyUsed(guid) {
      for (let filter of this.dynamicFilters) {
        if (filter.Value === guid) return true;
      }
      return false;
    },
    isTypeEnabled(type, filter) {
      if (type === "LocationProperty") return true;
      // else if (
      //   type === "City" &&
      //   (!this.isCountryFilterDefined || filter.Type === "Country")
      // )
      //   return false;

      // Shown Regions depending on countries
      let countryFilterIndex = _.findIndex(this.dynamicFilters, {
        Type: "Country",
      });
      if (type === "Region")
        if (
          countryFilterIndex < 0 ||
          (countryFilterIndex >= 0 &&
            !this.dynamicFilters[countryFilterIndex].Value) ||
          (countryFilterIndex >= 0 &&
            this.dynamicFilters[countryFilterIndex].Value &&
            this.dynamicFilters[countryFilterIndex].Value.length < 1)
        )
          return false;

      for (let filter of this.dynamicFilters) {
        if (filter.Type === type) return false;
      }
      return true;
    },
    // setCitiesOfSelectedCountriesForCityFilter() {
    //   let cityFilter = _.find(this.dynamicFilters, f => f.Type === "City");
    //   if (!cityFilter) return;

    //   let cities = [];
    //   let countryFilter = _.find(
    //     this.dynamicFilters,
    //     f => f.Type === "Country"
    //   );
    //   if (countryFilter && countryFilter.Value) {
    //     countryFilter.Value.forEach(entry => {
    //       let cityList = _.find(this.cityLists, c => c.CountryCode === entry);
    //       if (cityList && cityList.Cities)
    //         cities = _.union(cities, cityList.Cities);
    //     });
    //     cities = _.sortBy(cities, [c => c.City.toLowerCase()]);
    //   }
    //   cityFilter.currentDynamicFilterValues = cities;
    //   cityFilter.secondLevelSelectVisible = false;

    //   this.$nextTick(() => {
    //     cityFilter.secondLevelSelectVisible = true;
    //     this.$forceUpdate();
    //   });
    // },
    dynamicFilterTypeChanged(filter) {
      delete filter.Value;
      delete filter.ValuesValue;
      delete filter.currentDynamicFilterValues;
      delete filter.currentDynamicFilterValueValues;
      delete filter.secondLevelSelectVisible;
      delete filter.thirdLevelSelectVisible;

      switch (filter.Type) {
        case "Country":
          filter.currentDynamicFilterValues = this.countryList;
          break;
        case "Region":
          filter.currentDynamicFilterValues = this.regionsFiltered;
          break;
        case "City":
        case "PostalCode":
        case "Location":
        case "Check":
          filter.currentDynamicFilterValues = [];
          break;
        case "LocationProperty":
          filter.currentDynamicFilterValues = this.locationPropertiesList;
          break;
        case "Scenario":
          filter.currentDynamicFilterValues = this.scenariosOfSelectedWave;
          break;
        case "Status":
          filter.currentDynamicFilterValues = this.recruitingStatusList;
          break;
        default:
          return;
      }

      filter.secondLevelSelectVisible = true;
    },
    dynamicFilterValueChanged(filter) {
      // Forces select2 to rerender with new options
      delete filter.Subvalue;
      delete filter.thirdLevelSelectVisible;

      if (
        ["Status", "City", "PostalCode", "Country", "Location"].includes(
          filter.Type
        )
      ) {
        // if country value empty (e.g. after deselect) remove all regions
        if (
          filter.Type === "Country" &&
          (!filter.Value || filter.Value.length < 1)
        ) {
          _.remove(this.dynamicFilters, (f) => f.Type === "Region");
        } else if (filter.Type === "Country") {
          if (!filter.Value || filter.Value.length < 1) return;

          let regionFilter = this.dynamicFilters.find(
            (el) => el.Type === "Region"
          );
          if (regionFilter)
            regionFilter.currentDynamicFilterValues = this.regionsFiltered;
        }

        this.getCheckFilterResult();
      } else {
        this.$nextTick(() => {
          if (!filter.Value) return;
          // check if we have "LocationPropertyGuid"
          let currVal = filter.currentDynamicFilterValues.find(
            (val) => val[`${filter.Type}Guid`] === filter.Value
          );

          if (
            currVal &&
            currVal[`${filter.Type}Values`] &&
            currVal[`${filter.Type}Values`].length > 0
          ) {
            filter.currentDynamicFilterValueValues =
              currVal[`${filter.Type}Values`];
            filter.thirdLevelSelectVisible = true;
            this.$forceUpdate();
          } else this.getCheckFilterResult();
        });
      }
    },
    getRegionsByCountryCode(countryCode) {
      return this.regionList
        .filter((region) => {
          let result = false;

          if (region.CountryCode.toLowerCase() === countryCode.toLowerCase()) {
            result = true;
          }
          return result;
        })
        .map((region) => {
          return region.Regions;
        })
        .flat();
    },
    collectFilterData() {
      let result = {
        ClientGuid: this.selectedClient,
        ProjectGuid: this.selectedProject,
        WaveGuid: this.selectedWave,
      };
      if (this.selectedImporter) result.ImporterGuid = this.selectedImporter;

      for (let filter of this.dynamicFilters) {
        if (!filter.Type || !filter.Value) continue;

        if (filter.Type === "Country") {
          if (!filter.Value || filter.Value.length < 1) continue;
          result.CountryCodes = filter.Value;
        } else if (filter.Type === "Region") {
          if (!filter.Value || filter.Value.length < 1) continue;
          result.RegionGuids = filter.Value;
        } else if (filter.Type === "City") {
          if (!filter.Value || filter.Value.length < 1) continue;
          result.Cities = filter.Value;
        } else if (filter.Type === "PostalCode") {
          if (!filter.Value || filter.Value.length < 1) continue;
          result.PostalCodes = filter.Value;
        } else if (filter.Type === "Location") {
          if (!filter.Value || filter.Value.length < 1) continue;
          result.LocationStoreIds = filter.Value;
        } else if (filter.Type === "LocationProperty") {
          if (!filter.Value || !filter.Subvalue || filter.Subvalue.length < 1)
            continue;
          if (!result.LocationProperties) result.LocationProperties = [];
          result.LocationProperties.push({
            LocationPropertyGuid: filter.Value,
            LocationPropertyValues: filter.Subvalue,
          });
        } else if (filter.Type === "Check") {
          if (!filter.Value || filter.Value.length < 1) continue;
          result.SurveyInstanceIds = filter.Value.map((value) =>
            parseInt(value)
          );
        } else if (filter.Type === "Scenario") {
          if (!filter.Value || filter.Value.length < 1) continue;
          result.ScenarioGuids = filter.Value;
        } else if (filter.Type === "Status") {
          if (!filter.Value || filter.Value.length < 1) continue;
          result.SurveyInstanceRecruitingStatus = filter.Value;
        }
      }

      this.$emit("filterUpdate", result);
    },
    getLocationsOfClient() {
      this.$emit("requestLocations", this.selectedClient);
    },
    getLocationProperties() {
      this.$emit("requestLocationProperties", this.selectedClient);
    },
    getCheckFilterResult() {
      if (this.$filterRestorePending || !this.selectedWave) return;
      this.collectFilterData();
      let query = `api/Recruiting/SurveyInstances`;
      let postData = this.currentFilterObject;

      $.ajax({
        method: "POST",
        url: query,
        data: JSON.stringify(postData),
        dataType: "json",
        contentType: "application/json; charset=utf-8",
        success: (data) => {
          this.resultCheckData = data;
        },
        error: () => {
          console.error("Error on POST in getCheckFilterResult()");
          this.globals.showErrorSnackbar(this.lang.RC0009);
        },
      });
    },
    // getCityList(CountryCode) {
    //   let query = `api/Data/CitiesOfCountry/${CountryCode}`;
    //   $.ajax({
    //     method: "GET",
    //     url: query,
    //     success: data => {
    //       this.cityLists.push(data);
    //       this.setCitiesOfSelectedCountriesForCityFilter();
    //     },
    //     error: () => {
    //       console.error("Error on GET in getCityList()");
    //       this.globals.showErrorSnackbar(this.lang.RC0010);
    //     }
    //   });
    // }
  },
};
</script>

<style scoped>
.panel {
  height: 100%;
  margin: 0;
}
.panel-heading {
  height: 30px;
  padding: 5px;
  font-weight: bold;
  font-size: 18px;
  background: rgb(212, 245, 208) !important;
}
.panel-heading > span {
  font-size: 14px;
}
.panel-body {
  height: calc(50vh - 85px);
  overflow-y: auto;
}

#checkCounterContainer {
  position: absolute;
  right: 10px;
  font-weight: 300;
  color: rgb(17, 0, 201);
}
#checkCounterContainer.text-red {
  cursor: pointer;
}
#checkCounterContainer.text-red > span {
  position: relative;
  top: -8px;
}
#checkCounterContainer .material-icons {
  position: relative;
  top: -2px;
}

.form-group {
  overflow: hidden;
  margin: 0;
  padding: 0 5px;
}

.form-group-dynamic {
  border: 1px solid #ccc;
  border-radius: 10px;
  padding: 0;
  margin: 5px;
}

.btn-remove-filter {
  padding: 0px;
  margin: 10px;
}
.centerized-remove-button {
  bottom: 23px;
}

.btn-add-filter {
  margin: 10px;
}
.btn-add-filter > i {
  margin-left: -6px;
}
.vertical-center {
  margin: 0;
  position: absolute;
  top: 50%;
  left: 50%;
  -ms-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
}
</style>



// WEBPACK FOOTER //
// CheckFilter.vue?61262c44